import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { getCurrentClientId, saveClientId } from '../utils/client-route.util';
import { getActiveLanguage } from '../utils/active-language.util';
import { TranslationService } from '../services/translation.service';
import { MemberContextService } from '../../../@backoffice/services/member-context.service';
import { environment } from '../../../environments/environment';

function mapLanguageCodeToLocale(langCode: string): string {
  const languageMap: { [key: string]: string } = {
    'en': 'en',
    'es': 'es',
    'tr': 'tr',
    'fr': 'fr',
    'de': 'de',
    'it': 'it'
  };

  return languageMap[langCode.toLowerCase()] || `${langCode.toLowerCase()}-US`;
}

/**
 * HTTP Interceptor that adds X-Client-Id and Accept-Language headers to all outgoing requests,
 * plus x-campaign-id on backoffice API calls while a member campaign is selected.
 */
export const clientIdInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const translationService = inject(TranslationService);
  const clientId = getCurrentClientId(router);
  const acceptLanguage = mapLanguageCodeToLocale(
    getActiveLanguage(translationService.getCurrentLanguage() || 'en')
  );

  const headers: { [key: string]: string } = {
    'Accept-Language': acceptLanguage
  };

  if (clientId) {
    headers['X-Client-Id'] = clientId;
    saveClientId(clientId);
  } else {
    console.warn('Client ID not found in URL. Skipping X-Client-Id header injection.');
  }

  // Campaign picked in the member header. The backoffice API forwards it to the Loyalty API,
  // which needs the campaign GUID; only sent to our own API, never to other hosts. A campaign the
  // request already names (the verify-email link's) is kept.
  const campaignGuid = inject(MemberContextService).selectedCampaign()?.campaignGuid;
  if (campaignGuid && !req.headers.has('x-campaign-id') && req.url.startsWith(environment.apiBaseUrl)) {
    headers['x-campaign-id'] = campaignGuid;
  }

  return next(req.clone({ setHeaders: headers }));
};
